<template>
  <div class="table-content cal-height">
    <table v-if="tableData">
      <thead :class="{ 'edit-mode': isEditFirstColumnEditable }">
        <tr>
          <th
            v-if="isPartialEditMode"
            style="text-align: center"
            @click="toggleEditMode"
            class="table-cell"
          >
            <b-button type="is-success is-light" size="is-small">Edit</b-button>
          </th>
          <th v-else class="table-cell"></th>
          <th
            v-for="(header, index) in tableData.length > 0
              ? tableData[0].slice(1)
              : []"
            :key="`head-${index}`"
            class="table-cell"
            style="text-align: center"
            :contenteditable="isEditFirstColumnEditable"
            @input="handleHeaderInput($event, 0, index + 1)"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <transition-group name="row-bounce" tag="tbody">
        <tr
          v-for="(row, rowIndex) in tableData.slice(1)"
          :key="`row-${rowIndex}`"
          :class="{
            'odd-row': rowIndex % 2 !== 0,
            'even-row': rowIndex % 2 === 0,
          }"
        >
          <template v-for="(cell, colIndex) in row">
            <td
              v-if="colIndex === 0"
              class="table-cell"
              :class="{
                'editable-cell': isEditFirstColumnEditable && colIndex === 0,
                'editable-cell-background':
                  isEditFirstColumnEditable && colIndex === 0,
              }"
              :contenteditable="isCellContentEditable(colIndex)"
              @input="handleHeaderInput($event, rowIndex + 1, colIndex)"
              @blur="
                onCellLeave(rowIndex + 1, colIndex, $event.target.innerText)
              "
              @focus="onCellFocus(rowIndex + 1, colIndex)"
              v-html="cell"
            ></td>

            <td
              v-else
              class="table-cell"
              :class="{
                'editable-cell': isEditFirstColumnEditable && colIndex === 0,
                'editable-cell-background':
                  isEditFirstColumnEditable && colIndex === 0,
              }"
              @blur="
                onCellLeave(rowIndex + 1, colIndex, $event.target.innerText)
              "
              @focus="onCellFocus(rowIndex, colIndex)"
              tabindex="0"
            >
              <RichTextEditor
                v-model="row[colIndex]"
                :disabled="!isCellContentEditable(colIndex)"
                @input="(value) => handleInput(value, rowIndex + 1, colIndex)"
                :headings="[]"
                v-if="
                  rowIndex === focusedCell.row &&
                  colIndex === focusedCell.col &&
                  isCellContentEditable(colIndex)
                "
              />
              <DisplayRichTextEditor v-else :value="row[colIndex]" />
            </td>
          </template>
        </tr>
      </transition-group>
    </table>
  </div>
</template>

<script>
import RichTextEditor from '../../../RichTextEditor';
import DisplayRichTextEditor from '../../../RichTextEditor/DisplayRichTextEditor';

export default {
  props: {
    rows: {
      type: Number,
      required: true,
    },
    cols: {
      type: Number,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    displayType: {
      type: String,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCSV: {
      type: Boolean,
      required: false,
      default: false,
    },
    onlyNumbers: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  components: {
    RichTextEditor,
    DisplayRichTextEditor,
  },

  computed: {
    isInspectorMode() {
      return this.displayType === 'inspector';
    },
    isPartialEditMode() {
      return this.displayType === 'partialEdit';
    },
  },
  mounted() {
    this.isEditFirstColumnEditable = false;

    this.tableData = this.data;
    // Check if all rows have the same length, if not, pad with empty strings
    this.tableData.forEach((row) => {
      while (row.length < this.tableData[0].length) {
        row.push('');
      }
    });

    if (this.tableData.length === 0 || this.displayType === 'inspector') {
      this.createTableData();
    } else {
      this.initTempData();
    }
  },
  watch: {
    rows() {
      this.createTableData();
    },
    cols() {
      this.createTableData();
    },
    data(newData) {
      if (this.displayType === 'preview') {
        this.tableData = newData;
        this.initTempData();
      }
    },
  },
  data() {
    return {
      isEditFirstColumnEditable: false,
      tableData: [],
      tempTableData: [],
      focusedCell: { row: null, col: null },
    };
  },
  methods: {
    onCellFocus(row, col) {
      this.focusedCell = { row, col };
    },

    getCellEl(row, col) {
      return (
        this.$el.querySelectorAll('tbody tr')[row - 1]?.children[col] || null
      );
    },

    resetFocusedCell() {
      this.focusedCell = { row: null, col: null };
    },
    onCellLeave(rowIndex, colIndex, newValue) {
      this.$emit('onCellLeave', rowIndex, colIndex, newValue);
    },
    getTempTableData() {
      return this.tempTableData;
    },
    initTempData() {
      this.tempTableData = this.tableData.map((row) => [...row]);
    },
    handleInput(value, rowIndex, colIndex) {
      // const value = event.target.innerText;
      this.tempTableData[rowIndex][colIndex] = value;
      this.$emit('onCellInput', rowIndex, colIndex, value);
    },

    handleHeaderInput(event, rowIndex, colIndex) {
      const value = event.target.innerText;
      this.tempTableData[rowIndex][colIndex] = value;
      this.$emit('onCellInput', rowIndex, colIndex, value);
    },

    isCellContentEditable(colIndex) {
      if (this.readOnly) {
        return false;
      }

      if (
        this.isEditFirstColumnEditable &&
        this.isPartialEditMode &&
        colIndex === 0
      ) {
        return true;
      }

      if (this.isInspectorMode && colIndex !== 0) {
        return true;
      }

      return false;
    },
    toggleEditMode() {
      this.isEditFirstColumnEditable = !this.isEditFirstColumnEditable;
      if (!this.isEditFirstColumnEditable) {
        this.tableData = this.tempTableData.map((row) => [...row]);
        this.$emit('tableDataChanged', this.tableData);
      }
    },
    createTableData() {
      let oldTableData = [...this.tableData];
      if (this.tempTableData.length > 0) {
        oldTableData = [...this.tempTableData];
      }

      if (this.isInspectorMode) {
        // Use original data structure without headers/index column
        this.tableData = oldTableData.length
          ? oldTableData
          : Array.from({ length: this.rows }, () =>
              Array.from({ length: this.cols }, () => '')
            );
      } else {
        const headers = ['', ...Array.from({ length: this.cols }, () => '-')];

        this.tableData = [headers].concat(
          Array.from({ length: this.rows }, (_, rowIndex) =>
            Array.from({ length: this.cols + 1 }, (colValue, colIndex) =>
              colIndex === 0 ? String(rowIndex + 1) : ''
            )
          )
        );

        oldTableData.forEach((row, rowIndex) => {
          row.forEach((cell, cellIndex) => {
            if (
              this.tableData[rowIndex] &&
              this.tableData[rowIndex][cellIndex] !== undefined
            ) {
              this.tableData[rowIndex][cellIndex] = cell;
            }
          });
        });
      }

      this.initTempData();
      this.$emit('tableDataChanged', this.tableData);
    },
  },
};
</script>

<style scoped lang="scss">
.table-content {
  overflow: scroll;
  overflow-x: hidden;
  width: 100%;

  thead {
    position: sticky;
    top: -2px;
    background: #f9fafb;
    z-index: 2;
  }
}

.cal-height {
  max-height: calc(100vh - 570px);
}

.row-bounce-enter-active {
  animation: bounceIn 0.8s ease forwards;
}

.row-bounce-leave-active {
  transition: opacity 0.1s ease;
  opacity: 0;
}

.edit-mode,
.editable-cell-background {
  background-color: #e0ffe4;
}

thead.edit-mode th {
  background-color: #e0ffe4;
}
table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  border: #f5f5f5 3px solid;
}

.table-cell {
  padding: 8px;
  text-align: left;
  box-sizing: border-box;
}

.table-cell:not(:last-child) {
  border-right: 1px solid #ddd;
}

.table-cell:first-child {
  width: 100px;
  text-align: center;
  overflow: hidden;
}

.odd-row {
  background-color: #ffffff;
}

.even-row {
  background-color: #dddddd48;
}

@keyframes bounceIn {
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }
  30% {
    transform: translateY(0);
    opacity: 1;
  }
  50% {
    transform: translateY(-10px);
  }

  80% {
    transform: translateY(-0px);
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
